import { useState } from 'react';

import { FileType } from 'types';
import { useNavigate } from 'hooks';
import { Flex } from 'components/flex';
import { formatDate } from 'utils/date';
import { Image } from 'components/image';
import { capitalize } from 'utils/string';
import { Row, Col } from 'components/grid';
import { formatPrice } from 'utils/helpers';
import { Gallery } from 'components/gallery';
import { InfoBlock } from 'components/info-block';
import { GeneralInfo } from 'components/general-info';
import { BackButton } from 'components/specified-buttons';
import { PieChartLegend } from 'components/pie-chart-legend';
import { Avatar, AvatarPlaceholderType } from 'components/avatar';

import * as Styles from './styles';
import { AccountingExpensesDetailsProps } from './types';

const titles = ['Event', 'Amount', '% from total'];

export const AccountingExpensesDetails = ({
  data,
  hideEdit,
  disableSticky,
  hideBackButton,
}: AccountingExpensesDetailsProps) => {
  const navigate = useNavigate();
  const [isOpen, setOpen] = useState(false);

  return (
    <>
      {!hideBackButton && <BackButton text="View Expense" />}

      <GeneralInfo
        withoutPhoto
        name="Expense"
        withPlaceholders
        stickyWithHeader={false}
        sectionWrapper={InfoBlock}
        disableSticky={disableSticky}
        containerStyles={{ gap: '16px' }}
        onEdit={!hideEdit ? () => navigate('edit') : undefined}
        sections={[
          {
            rows: [
              [
                {
                  col: 3,
                  colTablet: 6,
                  title: 'Payment Date',
                  value: formatDate(data?.paymentDate),
                },
                {
                  col: 3,
                  colTablet: 6,
                  title: 'Amount',
                  value: formatPrice(data?.amount),
                },
                {
                  col: 3,
                  colTablet: 6,
                  title: 'Categories',
                  value: data?.categories
                    ?.map((category) => category.name)
                    .join(', '),
                },
                {
                  col: 3,
                  colTablet: 6,
                  title: 'Payment Method',
                  value: capitalize(data?.paymentMethod as string),
                },
              ],
            ],
          },
        ]}
      />

      <PieChartLegend
        title="Events"
        titles={titles}
        mobileTitles={titles}
        data={
          data?.events.map((event) => ({
            label: event.amount,
            id: event.event.name,
            value: event.percent,
          })) || []
        }
      />

      <Flex gap={24} flexDirection="column">
        <InfoBlock title="Employees">
          <Row>
            {data?.employees?.map((employee) => (
              <Col col={6} key={employee.id}>
                <Flex gap={16}>
                  <Avatar
                    square
                    size={48}
                    url={employee?.user?.photoUrl}
                    userName={employee?.user?.name}
                    placeholder={AvatarPlaceholderType.UserAvatar}
                  />

                  <Flex flexDirection="column">
                    <p>{employee?.user?.name}</p>
                    <Styles.EventPercent>
                      {employee?.user?.role?.name}
                    </Styles.EventPercent>
                  </Flex>
                </Flex>
              </Col>
            ))}
          </Row>
        </InfoBlock>

        <InfoBlock title="Receipt and description">
          <Flex gap={16}>
            {data?.receiptUrl && (
              <Image
                width={136}
                height={136}
                objectFit="cover"
                alt="Receipt photo"
                src={data?.receiptUrl}
                onClick={() => setOpen(true)}
              />
            )}

            <p>{data?.description}</p>
          </Flex>
        </InfoBlock>
      </Flex>

      {data?.receiptUrl && (
        <Gallery
          open={isOpen}
          hideFooterButton
          close={() => setOpen(false)}
          files={[
            {
              id: 1,
              url: data.receiptUrl,
              type: FileType.Image,
              name: 'Receipt photo',
            },
          ]}
        />
      )}
    </>
  );
};
